"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var currencySymbols_1 = require("./constants/currencySymbols");
var configurations_1 = require("./configurations");
var types_1 = require("./types");
var PriceFormatter;
(function (PriceFormatter) {
    var nonDigit = /(\D)/;
    var whiteSpace = '\u00A0';
    var defaultConfig = {
        decimalPlaces: 0,
        currency: '',
        currencyDisplay: types_1.CurrencyDisplay.Symbol,
        isSpaceBetweenSymbolAndCurrency: false,
        shouldTrimDecimalPointsTrailingZeros: false,
        isArabicNegativeSignAfterNumber: false,
    };
    PriceFormatter.format = function (number, locale, configuration) {
        if (!locale)
            return handleError("format function, locale is not valid. locale: " + locale, '');
        var config = configuration || defaultConfig;
        var currency = config.currency, currencyDisplay = config.currencyDisplay, isArabicNegativeSignAfterNumber = config.isArabicNegativeSignAfterNumber;
        if (!currency)
            return formatNumber(number, locale, config);
        var formattedNumber = formatNumber(number, locale, config);
        var formatPrice = getPriceFormatter(locale, config);
        var currencySymbol = getCurrency(currency, currencyDisplay);
        if (number >= 0)
            return formatPrice(currencySymbol, formattedNumber);
        var negativeSignLocation = getNegativeSignLocation(locale, currency, currencyDisplay, isArabicNegativeSignAfterNumber);
        return addNegativeSignIfNeeded(negativeSignLocation, formatPrice, currencySymbol, formattedNumber);
    };
    PriceFormatter.formatToParts = function (number, locale, configuration) {
        if (!locale)
            return handleError("format function, locale is not valid. locale: " + locale, []);
        var config = configuration || defaultConfig;
        var currency = config.currency, currencyDisplay = config.currencyDisplay, isArabicNegativeSignAfterNumber = config.isArabicNegativeSignAfterNumber;
        var formattedNumber = formatNumber(number, locale, config);
        var nonCurrencyResult = splitToParts(formattedNumber, locale);
        if (!currency)
            return nonCurrencyResult;
        var formatPrice = getPricePartsFormatter(locale, config);
        var currencySymbol = getCurrency(currency, currencyDisplay);
        if (number >= 0)
            return formatPrice([{ type: 'currency', value: currencySymbol }], nonCurrencyResult);
        var negativeSignLocation = getNegativeSignLocation(locale, currency, currencyDisplay, isArabicNegativeSignAfterNumber);
        return addNegativeSignIfNeededFormatToParts(negativeSignLocation, formatPrice, currencySymbol, nonCurrencyResult);
    };
    var splitToParts = function (formattedNumber, locale) {
        var _a = findConfiguration(configurations_1.LOCALIZATION_FORMAT, locale), decimal = _a.decimal, thousands = _a.thousands;
        var numberParts = formattedNumber.split(nonDigit).filter(Boolean);
        var toToken = function (value) {
            if (value === '-')
                return { value: value, type: 'minusSign' };
            if (value === thousands)
                return { value: value, type: 'group' };
            return { value: value, type: 'integer' };
        };
        var decimalIndex = numberParts.indexOf(decimal);
        if (decimalIndex === -1)
            return numberParts.map(toToken);
        var wholeNumbers = numberParts.slice(0, decimalIndex).map(toToken);
        var decimalToken = { value: numberParts[decimalIndex], type: 'decimal' };
        var fraction = { value: numberParts[decimalIndex + 1], type: 'fraction' };
        return wholeNumbers.concat([decimalToken, fraction]);
    };
    var getCurrencyLocation = function (config, locale) {
        var currencyLocationConfig = config.currencyDisplay === types_1.CurrencyDisplay.Code ? configurations_1.CURRENCY_CODE_LOCATION : configurations_1.CURRENCY_SYMBOL_LOCATION;
        var currencyLocation = findConfiguration(currencyLocationConfig, locale);
        if (!config.isSpaceBetweenSymbolAndCurrency)
            return currencyLocation;
        if (currencyLocation === types_1.CurrencyLocation.pre)
            return types_1.CurrencyLocation.preSpace;
        if (currencyLocation === types_1.CurrencyLocation.post)
            return types_1.CurrencyLocation.postSpace;
        return currencyLocation;
    };
    var getPriceFormatter = function (locale, config) {
        var currencyLocation = getCurrencyLocation(config, locale);
        switch (currencyLocation) {
            case types_1.CurrencyLocation.pre:
                return function (currency, number) { return "" + currency + number; };
            case types_1.CurrencyLocation.preSpace:
                return function (currency, number) { return currency + " " + number; };
            case types_1.CurrencyLocation.post:
                return function (currency, number) { return "" + number + currency; };
            default:
                return function (currency, number) { return number + " " + currency; };
        }
    };
    var getPricePartsFormatter = function (locale, config) {
        var currencyLocation = getCurrencyLocation(config, locale);
        var spaceToken = { type: 'literal', value: whiteSpace };
        switch (currencyLocation) {
            case types_1.CurrencyLocation.pre:
                return function (currency, numbers) { return currency.concat(numbers); };
            case types_1.CurrencyLocation.preSpace:
                return function (currency, numbers) { return currency.concat([spaceToken], numbers); };
            case types_1.CurrencyLocation.post:
                return function (currency, numbers) { return numbers.concat(currency); };
            default:
                return function (currency, numbers) { return numbers.concat([spaceToken], currency); };
        }
    };
    var getDefaultConfig = function (currency) { return ({
        display: currency,
        type: types_1.CurrencyType.code,
    }); };
    var getCurrencyConfig = function (currency, currencyDisplay) {
        if (currencyDisplay === types_1.CurrencyDisplay.Code) {
            return getDefaultConfig(currency.toUpperCase());
        }
        var normalizedCurrency = currency.toLowerCase();
        if (currencyDisplay === types_1.CurrencyDisplay.AppSymbol && currencySymbols_1.APP_CURRENCY_CONFIG[normalizedCurrency]) {
            return currencySymbols_1.APP_CURRENCY_CONFIG[normalizedCurrency];
        }
        return currencySymbols_1.CURRENCY_CONFIG[normalizedCurrency] || getDefaultConfig(currency);
    };
    var getNegativeSignLocation = function (locale, currency, currencyDisplay, isArabicNegativeSignAfterNumber) {
        if (isArabicNegativeSignAfterNumber === void 0) { isArabicNegativeSignAfterNumber = false; }
        var currencyType = getCurrencyConfig(currency, currencyDisplay).type;
        var currencyLocationConfig = currencyType === types_1.CurrencyType.symbol
            ? isArabicNegativeSignAfterNumber
                ? configurations_1.NEGATIVE_SYMBOL_LOCATION_FOR_CURRENCY_SYMBOL_EXP
                : configurations_1.NEGATIVE_SYMBOL_LOCATION_FOR_CURRENCY_SYMBOL
            : configurations_1.NEGATIVE_SYMBOL_LOCATION_FOR_CURRENCY_CODE;
        return findConfiguration(currencyLocationConfig, locale);
    };
    var getCurrency = function (currency, currencyDisplay) {
        return getCurrencyConfig(currency, currencyDisplay).display;
    };
    var formatNumber = function (number, locale, config) {
        // Handle negative number when formatted with currency individually.
        if (number < 0 && config.currency) {
            number = Math.abs(number);
        }
        var format = getNumberFormatter(locale);
        var shouldTrimDecimal = config.shouldTrimDecimalPointsTrailingZeros;
        var decimalPlaces = getDefaultDecimalPlaces(config);
        var fixedDecimal = number.toFixed(decimalPlaces);
        if (!decimalPlaces || !shouldTrimDecimal)
            return format(fixedDecimal);
        // Casting to number to remove trailing zeros
        var trimmedDecimal = Number(fixedDecimal).toString();
        return format(trimmedDecimal);
    };
    var getNumberFormatter = function (locale) {
        var separator = findConfiguration(configurations_1.LOCALIZATION_FORMAT, locale);
        return function (number) { return replaceSeparators(separator, number); };
    };
    var replaceSeparators = function (separator, numberString) {
        var _a = numberString.split('.'), number = _a[0], fraction = _a[1];
        var formattedNumber = number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + separator.thousands);
        return fraction ? [formattedNumber, fraction].join(separator.decimal) : formattedNumber;
    };
    var handleError = function (message, defaultValue) {
        if (console && typeof console.error === 'function')
            console.error("Error occur in PriceFormatter " + message);
        return defaultValue;
    };
    var getDefaultDecimalPlaces = function (_a) {
        var currency = _a.currency, decimalPlaces = _a.decimalPlaces;
        if (decimalPlaces !== -1)
            return decimalPlaces || 0;
        var currencyCode = currency && currency.toLowerCase() || handleError('missing configuration.currency', 'usd');
        var defaultDecimalPlaces = configurations_1.DEFAULT_DECIMAL_DIGIT[currencyCode];
        return (defaultDecimalPlaces == undefined) ? handleError("invalid configuration.currency. currency: " + currency, 2) : defaultDecimalPlaces;
    };
    var findConfiguration = function (config, locale) {
        locale = locale.toLowerCase();
        if (config.hasOwnProperty(locale))
            return config[locale];
        var prefixOnlyLocal = locale.match(/^\w+/);
        if (prefixOnlyLocal && prefixOnlyLocal.length && config.hasOwnProperty(prefixOnlyLocal[0])) {
            return config[prefixOnlyLocal[0]];
        }
        handleError("format function, locale is not valid. locale: " + locale, '');
        return config['en'];
    };
    var addNegativeSignIfNeeded = function (negativeSignLocation, formatPrice, currency, number) {
        switch (negativeSignLocation) {
            case types_1.NegativeSignLocation.withNumber:
                return formatPrice(currency, '-' + number);
            case types_1.NegativeSignLocation.afterNumber:
                return formatPrice(currency, number + '-');
            case types_1.NegativeSignLocation.withCurrency:
                return formatPrice('-' + currency, number);
            default:
                return formatPrice(currency, number);
        }
    };
    var addNegativeSignIfNeededFormatToParts = function (negativeSignLocation, formatPrice, currency, numbers) {
        var currencyToken = { type: 'currency', value: currency };
        var minusSignToken = { type: 'minusSign', value: '-' };
        switch (negativeSignLocation) {
            case types_1.NegativeSignLocation.withNumber:
                return formatPrice([currencyToken], [minusSignToken].concat(numbers));
            case types_1.NegativeSignLocation.afterNumber:
                return formatPrice([currencyToken], numbers.concat([minusSignToken]));
            case types_1.NegativeSignLocation.withCurrency:
                return formatPrice([minusSignToken, currencyToken], numbers);
            default:
                return formatPrice([currencyToken], numbers);
        }
    };
})(PriceFormatter = exports.PriceFormatter || (exports.PriceFormatter = {}));
//# sourceMappingURL=priceFormatter.js.map